import {
  Box,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  SvgIcon,
  Typography,
} from '@mui/material';
import {
  AppFormikButtonTiles,
  AppFormikTextField,
  ConfirmDialog,
} from 'shared/components/AppComponents';

import { ReactComponent as Search } from 'assets/images/ic_search.svg';
import { ReactComponent as Compliance } from 'assets/images/ic_compliance_tracking.svg';
import { Formik } from 'formik';
import EmptyPlaceholder from 'shared/components/EmptyPlaceholder';
import { useEffect, useMemo, useState } from 'react';
import { PoliciesService } from 'services';
import { Toast, Utils } from 'shared/helpers';
import * as uuid from 'uuid';
import ComplianceData from './ComplianceData';
import { FileRejection } from 'react-dropzone';
import ExternalLinkDialog from './ExternalLinkDialog';
import { saveAs } from 'file-saver';
import FrameworkAccordion from './FrameworkAccordion';

export interface IEvidence {
  '#': number;
  'ERL #': string;
  'Area of Focus': string;
  'Artifact Description': string;
  'SCF Control Mappings': string;
  'Documentation Artifact': string;
}

export interface ICriterion {
  criterion_no: number;
  erl_no: string;
  description: string;
  type?: string;
}

export interface IStatement {
  statement_no: number;
  title: string;
  type: string;
  criteria: ICriterion[];
}

export interface ISection {
  section_name: string;
  statements: IStatement[];
}

export interface IFrameworkControls {
  _id: string;
  scf_framework_key: string;
  isActive: boolean;
  sections: ISection[];
}

export interface IFramework {
  'SCF #': string;
  Frameworks: { [key: string]: string[] };
  'Evidence Request List': IEvidence[];
}

export interface ComplianceFrameworkScfMappings {
  scf_framework_key: any;
}

export interface ComplianceFrameworks {
  id: string;
  name: string;
  collection_type: 'Json' | 'DB';
  is_active: boolean;
  compliance_framework_scf_mappings: ComplianceFrameworkScfMappings;
  framework_controls: IFrameworkControls | null;
}

export interface PolicyComplianceFramework {
  framework_id: string;
  compliance_frameworks: ComplianceFrameworks;
}

export interface IPolicyFramework {
  id: string;
  policy_compliance_frameworks: PolicyComplianceFramework[];
}
export interface IVersion {
  version: string;
  evidenceList: IEvidence[];
  reference: string;

  statementNo?: number;
  statementTitle?: string;
  statementType?: string;
  sectionName?: string;
  collectionType?: 'Json' | 'DB';
}
export interface IFrameworkTracking {
  scfFrameworkKey: string;
  reference: string;
  versions: IVersion[];
}
export interface IAttachment {
  _id: string;
  file_name: string;
  external_url: string;
  created_at: string;
  file_size?: number;
  file_type?: string;
}
export interface IEvidenceTracking {
  _id: string;
  organizationId: string;
  completed_by: string;
  updatedBy: any;
  erl_number: string;
  is_completed: boolean;
  completed_at: string;
  notes: any;
  isActive: boolean;
  created_at: string;
  updated_at: string;
  __v: number;
  attachments?: Array<IAttachment>;
}

const ComplianceList: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [frameworks, setFrameworks] = useState<IFramework[]>([]);
  const [policyFrameworks, setPolicyFrameworks] = useState<IPolicyFramework[]>(
    []
  );
  const [frameworkTrackingList, setFrameworkTrackingList] = useState<
    IFrameworkTracking[]
  >([]);
  const [evidenceTrackings, setEvidenceTrackings] = useState<
    IEvidenceTracking[]
  >([]);
  const [erlNumberToUpdate, setErlNumberToUpdate] = useState('');
  const [noteText, setNoteText] = useState<{ [key: string]: string }>({});
  const [savingNote, setSavingNote] = useState<string>('');
  const [openExternalLink, setOpenExternalLink] = useState(false);
  const [currentComplianceTrackingId, setCurrentComplianceTrackingId] =
    useState<string>('');
  const [isUploading, setIsUploading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState<{
    id: string;
    name: string;
  } | null>(null);

  // Instance methods
  const totalFrameworkEvidences = (
    frameworkTracking: IFrameworkTracking
  ): number =>
    frameworkTracking.versions.reduce(
      (prev, current) => prev + current.evidenceList.length,
      0
    );
  const totalEvidenceFromFramework = (
    frameworkTracking: IFrameworkTracking
  ): number =>
    frameworkTracking.versions.reduce(
      (prev, current) =>
        prev +
        evidenceTrackings.filter(
          (eT) =>
            current.evidenceList
              .map((el) => el['ERL #'])
              .includes(eT.erl_number) && eT.is_completed
        ).length,
      0
    );
  const uniqueEvidences = useMemo(
    () =>
      frameworkTrackingList.reduce(
        (prev, current) => prev + totalFrameworkEvidences(current),
        10
      ),
    [frameworkTrackingList]
  );
  const evidenceCovered = useMemo(
    () =>
      frameworkTrackingList.reduce(
        (prev, current) => prev + totalEvidenceFromFramework(current),
        0
      ),
    [frameworkTrackingList, evidenceTrackings]
  );
  const evidenceCoverPercent = useMemo(() => {
    const percent = (evidenceCovered / uniqueEvidences) * 100;
    return Math.ceil(percent);
  }, [uniqueEvidences, evidenceCovered]);

  const canShowEvidence = (evidence: IEvidence, status: boolean[]): boolean => {
    if (status[0]) {
      return true;
    }
    if (status[2]) {
      const evi = evidenceTrackings.find(
        (e) => evidence['ERL #'] === e.erl_number
      );
      if (evi) {
        return !evi.is_completed;
      }
      return true;
    }
    return (
      evidenceTrackings.findIndex(
        (e) => evidence['ERL #'] === e.erl_number && e.is_completed
      ) > -1
    );
  };
  const totalEvidenceSelected = (version: IVersion): number =>
    evidenceTrackings.filter(
      (eT) =>
        version.evidenceList.map((el) => el['ERL #']).includes(eT.erl_number) &&
        eT.is_completed
    ).length;

  const canShowVersion = (version: IVersion, status: boolean[]): boolean => {
    if (status[0]) {
      return true;
    }
    if (status[2]) {
      return totalEvidenceSelected(version) < version.evidenceList.length;
    }
    return totalEvidenceSelected(version) > 0;
  };

  const prepareFrameworkTracking = (): void => {
    let scfFrameworkKeys: string[] = [];
    const scfFrameworkTypes: any = {};

    policyFrameworks.forEach((pf) => {
      const keys = pf.policy_compliance_frameworks.map(
        (p) =>
          p.compliance_frameworks.compliance_framework_scf_mappings
            .scf_framework_key
      );
      scfFrameworkKeys = [...scfFrameworkKeys, ...keys];
      pf.policy_compliance_frameworks.forEach((p) => {
        scfFrameworkTypes[
          p.compliance_frameworks.compliance_framework_scf_mappings.scf_framework_key
        ] = {
          type: p.compliance_frameworks.collection_type,
          framework_controls: p.compliance_frameworks?.framework_controls,
        };
      });
    });

    const frameworkTrackings: IFrameworkTracking[] = [];

    scfFrameworkKeys = Array.from(new Set(scfFrameworkKeys));

    for (let i = 0; i < scfFrameworkKeys.length; i += 1) {
      const scfFrameworkKey = scfFrameworkKeys[i];
      const frameworkTracking: IFrameworkTracking = {
        scfFrameworkKey,
        versions: [],
        reference: uuid.v4(),
      };

      if (scfFrameworkTypes[scfFrameworkKey].type === 'Json') {
        for (let j = 0; j < frameworks.length; j += 1) {
          const framework = frameworks[j];
          if (
            framework.Frameworks &&
            Object.hasOwn(framework.Frameworks, scfFrameworkKey)
          ) {
            const versions = framework.Frameworks[scfFrameworkKey];
            for (let k = 0; k < versions.length; k += 1) {
              const version = versions[k];
              if (framework['Evidence Request List'].length > 0) {
                frameworkTracking.versions.push({
                  version,
                  evidenceList: framework['Evidence Request List'],
                  reference: uuid.v4(),
                });
              }
            }
          }
        }
      }
      if (scfFrameworkTypes[scfFrameworkKey].type === 'DB') {
        const frameworkControls: IFrameworkControls =
          scfFrameworkTypes[scfFrameworkKey].framework_controls;
        frameworkControls.sections.forEach((section) => {
          section.statements.forEach((statement) => {
            frameworkTracking.versions.push({
              version: String(statement.statement_no),
              statementNo: statement.statement_no,
              statementTitle: statement.title,
              sectionName: section.section_name,
              collectionType: 'DB',
              reference: uuid.v4(),

              evidenceList: statement.criteria.map((criterion) => ({
                '#': criterion.criterion_no,
                'ERL #': criterion.erl_no,
                'Area of Focus': section.section_name,
                'Artifact Description': criterion.description,
                'SCF Control Mappings': scfFrameworkKey,
                'Documentation Artifact': statement.title,
              })),
            });
          });
        });
      }

      frameworkTrackings.push(frameworkTracking);
    }
    setFrameworkTrackingList(frameworkTrackings);
  };

  const isSelected = (evidence: IEvidence): boolean => {
    const index = evidenceTrackings.findIndex(
      (eT) => eT.erl_number === evidence['ERL #']
    );
    if (index > -1) {
      return evidenceTrackings[index].is_completed;
    }
    return false;
  };

  // API Calls
  const loadPolicyCompliancTracking = (): void => {
    PoliciesService.policyCompliancTracking()
      .then((response) => {
        const { data } = response.data;
        setPolicyFrameworks(data.policies);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setIsLoading(false);
      });
  };
  const getEvidenceComplianceTracking = (): void => {
    PoliciesService.getComplianceTracking()
      .then((response) => {
        const { data } = response.data;
        setEvidenceTrackings(data.tracking);
      })
      .catch((error) => console.log(error))
      .finally(() => {});
  };
  const loadFrameworkDetails = (): void => {
    setIsLoading(true);
    fetch('/staticjson/PolicyFramework.json', {
      method: 'GET',
    })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP error! ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setFrameworks(data);
        loadPolicyCompliancTracking();
      })
      .catch((err) => {
        console.error('Fetch error:', err);
        setIsLoading(false);
      });
  };

  // const handleEvidenceToggle = (erlNumber: string): void => {
  //   setExpandedEvidence(expandedEvidence === erlNumber ? '' : erlNumber);
  // };

  // UI Actions
  const actionOnChangeTracking = (
    status: boolean,
    evidence: IEvidence,
    notes?: string
  ): void => {
    const index = evidenceTrackings.findIndex(
      (eT) => eT.erl_number === evidence['ERL #']
    );

    const promise =
      index === -1
        ? PoliciesService.createComplianceTracking({
            erl_number: evidence['ERL #'],
            is_completed: status,
            notes: notes ?? evidenceTrackings[index]?.notes ?? null,
          })
        : PoliciesService.updateComplianceTracking(
            evidenceTrackings[index]._id,
            {
              is_completed: status,
              notes: notes ?? evidenceTrackings[index]?.notes ?? null,
            }
          );
    setErlNumberToUpdate(evidence['ERL #']);
    promise
      .then((response) => {
        const { data } = response.data;
        const index = evidenceTrackings.findIndex(
          (eT) => eT.erl_number === evidence['ERL #']
        );
        if (index > -1) {
          evidenceTrackings[index].is_completed = status;
          evidenceTrackings[index].notes = notes ?? '';
        } else {
          evidenceTrackings.push(data);
        }
        setEvidenceTrackings([...evidenceTrackings]);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setErlNumberToUpdate('');
        setSavingNote('');
      });
  };

  // Hooks
  useEffect(() => {
    loadFrameworkDetails();
    getEvidenceComplianceTracking();
  }, []);

  useEffect(() => {
    if (frameworks.length > 0 && policyFrameworks.length > 0) {
      prepareFrameworkTracking();
    }
  }, [frameworks, policyFrameworks]);

  const uploadAttachments = (files: any[], trackingId: string): void => {
    setIsUploading(true);

    Promise.all(
      files.map((file) => {
        const formData = new FormData();
        formData.append('compliance_tracking_id', trackingId);
        formData.append('file', file);

        return PoliciesService.createComplianceAttachment(formData);
      })
    )
      .then(() => {
        Toast.showSuccessMessage('Attachments uploaded successfully.');

        getEvidenceComplianceTracking();
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsUploading(false);
        setCurrentComplianceTrackingId('');
      });
  };

  const getTrackingId = (erlNumber: string): Promise<any> =>
    new Promise((resolve, reject) => {
      PoliciesService.createComplianceTracking({
        erl_number: erlNumber,
        is_completed: false,
        notes: '',
      })
        .then((response) => {
          const newTracking = response.data.data;

          setEvidenceTrackings((prev) => [...prev, newTracking]);
          resolve(newTracking);
        })
        .catch((error) => {
          reject(error);
        });
    });

  const actionOnFileSelected = (
    acceptedFiles: File[],
    rejectedFiles: FileRejection[],
    erlNumber: string
  ): void => {
    const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
    const MAX_FILES = 5;

    if (acceptedFiles.length > MAX_FILES) {
      Toast.showInfoMessage(`You can upload a maximum of ${MAX_FILES} files.`);
      return;
    }

    if (acceptedFiles.some((file) => file.size > MAX_SIZE)) {
      Toast.showInfoMessage('One or more files exceed the 10 MB size limit.');
      return;
    }

    const trackingRecord = evidenceTrackings.find(
      (item) => item.erl_number === erlNumber
    );
    if (!trackingRecord) {
      setIsUploading(true);
      getTrackingId(erlNumber)
        .then((tracking) => {
          uploadAttachments(acceptedFiles, tracking?._id);
        })
        .catch((error) => {
          Utils.handleErrorResponse(error);
        });
    } else {
      uploadAttachments(acceptedFiles, trackingRecord?._id);
    }
  };

  const getInitialNote = (erlNumber: string): string => {
    const tracking = evidenceTrackings.find((e) => e.erl_number === erlNumber);
    return tracking?.notes || '';
  };

  // Function to handle note change
  const actionOnNoteChange = (erlNumber: string, value: string): void => {
    setNoteText((prev) => ({
      ...prev,
      [erlNumber]: value,
    }));
  };

  // Function to handle cancel
  const actionOnCancelNote = (erlNumber: string): void => {
    const initialNote = getInitialNote(erlNumber);
    setNoteText((prev) => ({
      ...prev,
      [erlNumber]: initialNote,
    }));
  };

  const getNoteValue = (erlNumber: string): string => {
    if (noteText[erlNumber] !== undefined) {
      return noteText[erlNumber];
    }
    // Otherwise get from API data
    return getInitialNote(erlNumber);
  };

  const handleExternalAttachmentOpen = (evidence: any): void => {
    const trackingRecord = evidenceTrackings.find(
      (eT) => eT.erl_number === evidence['ERL #']
    );

    if (trackingRecord) {
      setCurrentComplianceTrackingId(trackingRecord._id);
      setOpenExternalLink(true);
    } else {
      getTrackingId(evidence['ERL #']).then((trackingRecord) => {
        setCurrentComplianceTrackingId(trackingRecord._id);
        setOpenExternalLink(true);
      });
    }
  };

  useEffect(() => {
    if (evidenceTrackings.length > 0) {
      const notesMap: { [key: string]: string } = {};
      evidenceTrackings.forEach((tracking) => {
        if (tracking.notes) {
          notesMap[tracking.erl_number] = tracking.notes;
        }
      });
      setNoteText((prev) => ({
        ...prev,
        ...notesMap,
      }));
    }
  }, [evidenceTrackings]);

  const handleAddExternalLink = (data: any): void => {
    const { title, url } = data;
    const payload = {
      compliance_tracking_id: currentComplianceTrackingId,
      external_url: url,
      file_name: title,
    };
    PoliciesService.createComplianceAttachment(payload)
      .then((response) => {
        const { data } = response.data;
        if (data) {
          Toast.showSuccessMessage('External link added successfully');
          setOpenExternalLink(false);

          getEvidenceComplianceTracking();
        }
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      })
      .finally(() => {
        setIsLoading(false);
        setCurrentComplianceTrackingId(''); // Reset
      });
  };

  const handleOpenDeleteModal = (
    attachmentId: string,
    attachmentName: string
  ): void => {
    setAttachmentToDelete({ id: attachmentId, name: attachmentName });
    setShowDeleteModal(true);
  };

  // Handle closing delete modal
  const handleCloseDeleteModal = (): void => {
    setShowDeleteModal(false);
    setAttachmentToDelete(null);
  };

  const handleDeleteAttachment = (): void => {
    if (!attachmentToDelete) return;

    PoliciesService.deleteComplianceAttachment(attachmentToDelete.id)
      .then((response) => {
        if (response.data.msg) {
          Toast.showSuccessMessage(response.data.msg);

          getEvidenceComplianceTracking();
        }
      })
      .catch((error) => {
        console.error('Failed to delete attachment:', error);
        Toast.showErrorMessage('Failed to delete attachment.');
      })
      .finally(() => {
        handleCloseDeleteModal();
      });
  };

  const handleDownload = (attachment: IAttachment): void => {
    if (attachment.file_type === 'external_url' && attachment.external_url) {
      window.open(attachment.external_url, '_blank', 'noopener,noreferrer');
      return;
    }
    PoliciesService.downloadAttachment(attachment._id)
      .then((response) => {
        const data = response.data?.data;
        if (data) {
          saveAs(data.downloadUrl, attachment.file_name);
          Toast.showSuccessMessage('Download started.');
        }
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
      });
  };

  return (
    <>
      {isLoading && (
        <Box className="empty-content">
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <>
          <ComplianceData
            overallPercentage={evidenceCoverPercent}
            evidanceCoverage={evidenceCovered}
            evidanceTotal={uniqueEvidences}
            activeFramework={frameworkTrackingList.length}
          />
          <Box className="compliance-wrapper">
            <Stack className="compliance-container" direction="column">
              <Formik
                enableReinitialize
                initialValues={{
                  'search-compliance': '',
                  status: [true, false, false],
                }}
                validateOnMount
                onSubmit={() => console.log('test')}
              >
                {({ values }) => (
                  <Paper variant="outlined">
                    <Grid container className="compliance-list-header">
                      <Grid item md={8} xs={12}>
                        <Box className="compliance-title">
                          <Typography variant="h4">
                            Evidence requirements
                          </Typography>
                          <Typography component="span">
                            Review and track compliance evidence across all your
                            frameworks
                          </Typography>
                        </Box>
                      </Grid>
                      {false && (
                        <Grid item md={4} xs={12}>
                          <Box className="policies-search">
                            <AppFormikTextField
                              inputProps={{
                                startAdornment: (
                                  <SvgIcon
                                    component={Search}
                                    inheritViewBox
                                    className="view-icon"
                                    fontSize="inherit"
                                    htmlColor="#76A81F"
                                  />
                                ),
                              }}
                              outerLabel={false}
                              placeholder="Search control or evidence .."
                              name="search-compliance"
                            />
                          </Box>
                        </Grid>
                      )}
                    </Grid>

                    <AppFormikButtonTiles
                      variant="outlined"
                      name="status"
                      size="small"
                      label=""
                      disabled={false}
                      multichoice={false}
                      className="app-group-button mt-5 mb-5"
                      buttons={[
                        {
                          value: 'All',
                        },
                        {
                          value: 'Complete',
                        },
                        {
                          value: 'Incomplete',
                        },
                      ]}
                      outerLabel
                    />

                    {frameworkTrackingList.length > 0 && (
                      <FrameworkAccordion
                        frameworkTrackingList={frameworkTrackingList}
                        evidenceTrackings={evidenceTrackings}
                        erlNumberToUpdate={erlNumberToUpdate}
                        savingNote={savingNote}
                        setSavingNote={setSavingNote}
                        isUploading={isUploading}
                        status={values.status}
                        onTrackingChange={actionOnChangeTracking}
                        onNoteChange={actionOnNoteChange}
                        onCancelNote={actionOnCancelNote}
                        onFileSelected={actionOnFileSelected}
                        onExternalLinkOpen={handleExternalAttachmentOpen}
                        onDeleteAttachment={handleOpenDeleteModal}
                        onDownloadAttachment={handleDownload}
                        getNoteValue={getNoteValue}
                        getInitialNote={getInitialNote}
                        isSelected={isSelected}
                        canShowEvidence={canShowEvidence}
                        canShowVersion={canShowVersion}
                        totalEvidenceSelected={totalEvidenceSelected}
                      />
                    )}
                    {frameworkTrackingList.length === 0 && (
                      <EmptyPlaceholder
                        icon={
                          <SvgIcon
                            component={Compliance}
                            inheritViewBox
                            className="view-icon"
                            fontSize="inherit"
                            htmlColor="#76A81F"
                          />
                        }
                        title="No tracked frameworks"
                        message="To start tracking compliance, publish an approved policy with a compliance framework. If that compliance framework has been mapped with compliance tracking, this page will reflect that."
                      />
                    )}
                  </Paper>
                )}
              </Formik>
            </Stack>
          </Box>

          <ConfirmDialog
            title="Delete attachment?"
            subTitle={`This will permanently delete "${
              attachmentToDelete?.name || ''
            }". This action cannot be undone.`}
            showConfirm={showDeleteModal}
            cancelTitle="Cancel"
            successTitle="Delete"
            onSuccess={handleDeleteAttachment}
            onCancel={handleCloseDeleteModal}
            onCloseDialog={handleCloseDeleteModal}
          />

          <ExternalLinkDialog
            open={openExternalLink}
            title="Add external link"
            onClose={() => setOpenExternalLink(false)}
            onSave={handleAddExternalLink}
          />
        </>
      )}
    </>
  );
};

export default ComplianceList;
